import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';
import { auth } from '../../config/firebase';
import { Card, Button, Spinner } from '../../components';
import { friendlyAuthError } from '../../services/auth-service';

type Status = 'working' | 'success' | 'error';

const CheckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const AlertIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0L3.19 16a2 2 0 001.74 3z" />
  </svg>
);

const EyeIcon: React.FC<{ open: boolean }> = ({ open }) =>
  open ? (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-bg flex items-center justify-center p-6">
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <img src="/logo.png" alt="BunyadKar" className="h-12 w-auto mx-auto" />
      </div>
      <Card>{children}</Card>
    </div>
  </div>
);

const AuthAction: React.FC = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode') || '';

  if (!mode || !oobCode) {
    return (
      <Shell>
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-danger/15 flex items-center justify-center mb-4">
            <AlertIcon className="w-7 h-7 text-danger" />
          </div>
          <h2 className="text-2xl heading mb-2">Invalid link</h2>
          <p className="text-muted mb-6">
            The link you followed isn't a valid BunyadKar action — it may have been mistyped or truncated.
          </p>
          <Link to="/login">
            <Button variant="primary" size="md" className="w-full">Back to sign in</Button>
          </Link>
        </div>
      </Shell>
    );
  }

  if (mode === 'verifyEmail') return <VerifyEmailFlow oobCode={oobCode} />;
  if (mode === 'resetPassword') return <ResetPasswordFlow oobCode={oobCode} />;
  if (mode === 'recoverEmail') return <RecoverEmailStub />;

  return (
    <Shell>
      <div className="text-center">
        <h2 className="text-2xl heading mb-2">Unknown action</h2>
        <p className="text-muted mb-6">
          The link uses an action this app doesn't recognize ({mode}).
        </p>
        <Link to="/login">
          <Button variant="primary" size="md" className="w-full">Back to sign in</Button>
        </Link>
      </div>
    </Shell>
  );
};

/* ──────────────────────────  verifyEmail  ────────────────────────── */

const VerifyEmailFlow: React.FC<{ oobCode: string }> = ({ oobCode }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('working');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await applyActionCode(auth, oobCode);
        if (!alive) return;
        setStatus('success');
      } catch (err: any) {
        if (!alive) return;
        setErrorMsg(friendlyAuthError(err) || 'This link is expired or already used.');
        setStatus('error');
      }
    })();
    return () => { alive = false; };
  }, [oobCode]);

  if (status === 'working') {
    return (
      <Shell>
        <div className="text-center py-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-chart/15 flex items-center justify-center mb-4">
            <Spinner className="w-7 h-7 text-chart" />
          </div>
          <h2 className="text-2xl heading mb-2">Verifying your email…</h2>
          <p className="text-muted">Hang tight, this only takes a second.</p>
        </div>
      </Shell>
    );
  }

  if (status === 'success') {
    return (
      <Shell>
        <div className="text-center py-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-success/15 flex items-center justify-center mb-4">
            <CheckIcon className="w-7 h-7 text-success" />
          </div>
          <h2 className="text-2xl heading mb-2">Email verified</h2>
          <p className="text-muted mb-6">
            Your email has been confirmed. Continue to BunyadKar to finish setting up your account.
          </p>
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => {
              // If a Firebase user is signed in here, /verify will auto-detect and route them.
              // Otherwise (clicked link on a different device), nudge to /login.
              navigate(auth.currentUser ? '/verify' : '/login');
            }}
          >
            Continue
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-center py-2">
        <div className="w-14 h-14 mx-auto rounded-full bg-danger/15 flex items-center justify-center mb-4">
          <AlertIcon className="w-7 h-7 text-danger" />
        </div>
        <h2 className="text-2xl heading mb-2">Link expired or already used</h2>
        <p className="text-muted mb-6">
          {errorMsg} If you've already verified your email, just sign in. Otherwise, sign in to request a fresh link.
        </p>
        <Link to="/login">
          <Button variant="primary" size="lg" className="w-full">Go to sign in</Button>
        </Link>
      </div>
    </Shell>
  );
};

/* ──────────────────────────  resetPassword  ────────────────────────── */

const ResetPasswordFlow: React.FC<{ oobCode: string }> = ({ oobCode }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('working');
  const [email, setEmail] = useState<string>('');
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const e = await verifyPasswordResetCode(auth, oobCode);
        if (!alive) return;
        setEmail(e);
        setStatus('success');
      } catch (err: any) {
        if (!alive) return;
        setErrorMsg(friendlyAuthError(err) || 'This link is expired or invalid.');
        setStatus('error');
      }
    })();
    return () => { alive = false; };
  }, [oobCode]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    if (!pwd || pwd.length < 6) {
      setErrorMsg('Password must be at least 6 characters.');
      return;
    }
    if (pwd !== confirm) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, pwd);
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: any) {
      setErrorMsg(friendlyAuthError(err) || 'Could not reset password — try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'working') {
    return (
      <Shell>
        <div className="text-center py-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-chart/15 flex items-center justify-center mb-4">
            <Spinner className="w-7 h-7 text-chart" />
          </div>
          <h2 className="text-2xl heading mb-2">Checking your reset link…</h2>
        </div>
      </Shell>
    );
  }

  if (status === 'error') {
    return (
      <Shell>
        <div className="text-center py-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-danger/15 flex items-center justify-center mb-4">
            <AlertIcon className="w-7 h-7 text-danger" />
          </div>
          <h2 className="text-2xl heading mb-2">Reset link expired</h2>
          <p className="text-muted mb-6">{errorMsg}</p>
          <Link to="/forgot">
            <Button variant="primary" size="lg" className="w-full">Request a new link</Button>
          </Link>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center py-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-success/15 flex items-center justify-center mb-4">
            <CheckIcon className="w-7 h-7 text-success" />
          </div>
          <h2 className="text-2xl heading mb-2">Password updated</h2>
          <p className="text-muted">Redirecting you to sign in…</p>
        </div>
      </Shell>
    );
  }

  const inputBase = 'w-full px-4 py-2.5 rounded-card border bg-shell text-text placeholder:text-muted/50 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart transition-all text-sm';

  return (
    <Shell>
      <div className="mb-6 text-center">
        <h2 className="text-2xl heading mb-1">Set a new password</h2>
        <p className="text-muted text-sm">For <strong>{email}</strong></p>
      </div>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label className="block text-xs font-medium text-text mb-1.5">New password</label>
          <div className="relative">
            <input
              type={showPwd ? 'text' : 'password'}
              value={pwd}
              onChange={e => setPwd(e.target.value)}
              placeholder="Min. 6 characters"
              className={`${inputBase} pr-10 border-line`}
            />
            <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text">
              <EyeIcon open={showPwd} />
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-text mb-1.5">Confirm password</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              className={`${inputBase} pr-10 border-line`}
            />
            <button type="button" tabIndex={-1} onClick={() => setShowConfirm(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text">
              <EyeIcon open={showConfirm} />
            </button>
          </div>
        </div>
        {errorMsg && <p className="text-danger text-xs">{errorMsg}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-chart text-white py-2.5 rounded-card font-medium hover:bg-chart/90 transition-all duration-200 shadow-sm text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting && <Spinner className="w-4 h-4" />}
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </Shell>
  );
};

/* ──────────────────────────  recoverEmail (stub)  ────────────────────────── */

const RecoverEmailStub: React.FC = () => (
  <Shell>
    <div className="text-center py-2">
      <h2 className="text-2xl heading mb-2">Email change isn't supported yet</h2>
      <p className="text-muted mb-6">
        Contact <strong>info.bunyadkar@gmail.com</strong> if you need help recovering your email.
      </p>
      <Link to="/login">
        <Button variant="primary" size="md" className="w-full">Back to sign in</Button>
      </Link>
    </div>
  </Shell>
);

export default AuthAction;
